<template>
  <PageLayout>
    <template #headerTitle>Gestion des administrateurs</template>

    <VBusHelper :emitOnMounted="$constants.EVENTS.ADMINS.LIST_ALL.REFRESH" />

    <!-- HEADER WITH CREATE BUTTON -->
    <div class="flex justify-between items-center mb-6">
      <div class="text-gray-600">
        {{ admins.length }} administrateur(s)
      </div>
      <button
        class="px-6 py-2.5 rounded-xl bg-primary-600 text-white font-medium hover:bg-primary-700 transition-all"
        @click="openEditor(null)"
      >
        + Nouvel administrateur
      </button>
    </div>

    <!-- ERROR STATE -->
    <div
      v-if="error"
      class="bg-red-50 rounded-2xl p-8 text-center"
    >
      <div class="text-red-600 mb-4">{{ error }}</div>
      <button
        class="px-6 py-2 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700"
        @click="$bus.emit($constants.EVENTS.ADMINS.LIST_ALL.REFRESH)"
      >
        Réessayer
      </button>
    </div>

    <!-- ADMINS GRID -->
    <div
      v-else-if="admins.length > 0"
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
    >
      <div
        v-for="admin in admins"
        :key="admin.id"
        class="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all"
      >
        <!-- HEADER -->
        <div class="bg-gradient-to-r from-primary-500 to-primary-600 p-6 text-white">
          <div class="flex items-center justify-between">
            <div class="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-2xl font-bold">
              {{ getInitials(admin.firstName, admin.lastName) }}
            </div>
            <div class="flex gap-2">
              <button
                class="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-all"
                @click="openEditor(admin)"
                title="Modifier"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                class="p-2 rounded-lg bg-white/20 hover:bg-red-400 transition-all"
                @click="confirmDelete(admin)"
                title="Supprimer"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>
          <div class="mt-4">
            <div class="font-semibold text-lg truncate">{{ admin.firstName }} {{ admin.lastName }}</div>
            <div class="text-sm opacity-90 truncate">{{ admin.email }}</div>
          </div>
        </div>

        <!-- BODY -->
        <div class="p-4 space-y-4">
          <!-- FEATURES -->
          <div>
            <div class="text-xs text-gray-500 mb-2">Fonctionnalités autorisées</div>
            <div class="flex flex-wrap gap-1">
              <span
                v-for="feature in getAdminFeatures(admin)"
                :key="feature"
                class="px-2 py-1 bg-green-100 text-green-700 rounded-lg text-xs font-medium"
              >
                {{ $constants.ADMIN.AVAILABLE_FEATURES[feature] || feature }}
              </span>
              <span
                v-if="!getAdminFeatures(admin).length"
                class="px-2 py-1 bg-gray-100 text-gray-500 rounded-lg text-xs"
              >
                Aucune
              </span>
            </div>
          </div>

          <!-- COUNTRIES -->
          <div>
            <div class="text-xs text-gray-500 mb-2">Pays autorisés</div>
            <div class="flex flex-wrap gap-1">
              <span
                v-for="country in getAdminCountries(admin)"
                :key="country"
                class="px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium"
              >
                {{ $constants.ADMIN.AVAILABLE_COUNTRIES[country] || country }}
              </span>
              <span
                v-if="!getAdminCountries(admin).length"
                class="px-2 py-1 bg-gray-100 text-gray-500 rounded-lg text-xs"
              >
                Aucun
              </span>
            </div>
          </div>

          <!-- ACTIONS -->
          <div class="flex gap-2 pt-2 border-t border-gray-200">
            <button
              class="flex-1 px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-all"
              @click="openEditor(admin)"
            >
              Modifier
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- LOADING STATE -->
    <div
      v-else-if="loading"
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
    >
      <div
        v-for="i in 4"
        :key="i"
        class="bg-white rounded-2xl overflow-hidden shimmerContainer"
      >
        <div class="h-40 bg-gray-200 shimmerTarget"></div>
        <div class="p-4 space-y-3">
          <div class="h-4 bg-gray-200 rounded shimmerTarget"></div>
          <div class="h-3 bg-gray-200 rounded shimmerTarget"></div>
          <div class="h-3 bg-gray-200 rounded shimmerTarget w-2/3"></div>
        </div>
      </div>
    </div>

    <!-- EMPTY STATE -->
    <div
      v-else
      class="bg-white rounded-2xl p-12 text-center"
    >
      <div class="text-6xl mb-4">👥</div>
      <h3 class="text-xl font-semibold text-gray-900 mb-2">Aucun administrateur</h3>
      <p class="text-gray-500 mb-6">Commencez par ajouter votre premier administrateur</p>
      <button
        class="px-6 py-2.5 rounded-xl bg-primary-600 text-white font-medium hover:bg-primary-700"
        @click="openEditor(null)"
      >
        + Ajouter un administrateur
      </button>
    </div>
  </PageLayout>
</template>

<script>
export default {
  name: 'AdminsPage',

  data() {
    return {
      admins: [],
      loading: false,
      error: null
    };
  },

  mounted() {
    this.setupEventListeners();
  },

  beforeUnmount() {
    this.cleanupEventListeners();
  },

  methods: {
    setupEventListeners() {
      this.$bus.on(this.$constants.EVENTS.ADMINS.LIST_ALL.REFRESHED, (admins) => {
        this.admins = admins || [];
        this.loading = false;
        this.error = null;
      });

      this.$bus.on(this.$constants.EVENTS.ADMINS.LIST_ALL.REFRESH_ERROR, (error) => {
        this.error = error;
        this.loading = false;
      });

      this.$bus.on(this.$constants.EVENTS.ADMINS.LIST_ALL.REFRESHING, () => {
        this.loading = true;
      });
    },

    cleanupEventListeners() {
      this.$bus.off(this.$constants.EVENTS.ADMINS.LIST_ALL.REFRESHED);
      this.$bus.off(this.$constants.EVENTS.ADMINS.LIST_ALL.REFRESH_ERROR);
      this.$bus.off(this.$constants.EVENTS.ADMINS.LIST_ALL.REFRESHING);
    },

    getInitials(firstName, lastName) {
      const first = firstName?.charAt(0) || '';
      const last = lastName?.charAt(0) || '';
      return (first + last).toUpperCase();
    },

    getAdminFeatures(admin) {
      return admin.metadata?.features || [];
    },

    getAdminCountries(admin) {
      return admin.metadata?.countries || [];
    },

    openEditor(admin) {
      this.$bus.emit(this.$constants.EVENTS.ADMINS.CREATE_OR_UPDATE.SHOW, admin);
    },

    async confirmDelete(admin) {
      const result = await window.customPrompt({
        title: 'Supprimer l\'administrateur',
        message: `Êtes-vous sûr de vouloir supprimer ${admin.firstName} ${admin.lastName} ?`,
        successText: 'Supprimer',
        cancelText: 'Annuler',
        withInput: false
      });

      if (result?.accepted) {
        this.$bus.emit(this.$constants.EVENTS.ADMINS.DELETE.DELETING, admin.id);
      }
    }
  }
};
</script>
